import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useConstant } from "@hooks/useConstant";
import { View, ViewDensity } from "@common/types/views/views";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { pluralizeWithCount } from "@common/helpers/string";
import { preferCodeText } from "@common/helpers/accounting";
import {
    EliminationExceptionReason,
    EliminationFactType,
} from "@common/types/domains/accounting/elimination";
import { useEliminationExceptionsReportQuery } from "@api/accountingReports.api";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { useOverlays } from "contexts/OverlayContext";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { getOptionalReportDateRangeLabel } from "@common/helpers/reports";
import { AccountingReportFooter } from "../AccountingReportFooter";
import {
    buildLocalReportChrome,
    useGeneratedReportFooterText,
} from "../localReportChrome";
import { useAccountingTabContext } from "../useAccountingTab";
import { useConsolidatedReportView } from "../useConsolidatedReportView";
import { EliminatingEntryDrawer } from "../eliminatingEntryDrawer/EliminatingEntryDrawer";
import { GlDrilldownDrawer } from "../generalLedger/GlDrilldownDrawer";
import { useGlDrilldownDrawerState } from "../useGlDrilldownDrawerState";
import {
    EliminationExceptionsColumn,
    EliminationExceptionsTableMeta,
    EliminationExceptionsViewRow,
    eliminationExceptionsViewConfig,
} from "./eliminationExceptionsViewConfig";
import styles from "./EliminationExceptionsTable.module.scss";

const defaultView: View = {
    columns: [
        { id: EliminationExceptionsColumn.DATE, options: { pin: "left" } },
        { id: EliminationExceptionsColumn.SOURCE },
        { id: EliminationExceptionsColumn.REASON },
        { id: EliminationExceptionsColumn.ENTITY },
        { id: EliminationExceptionsColumn.COUNTERPARTY_ENTITY },
        { id: EliminationExceptionsColumn.ACCOUNT },
        { id: EliminationExceptionsColumn.AMOUNT, options: { pin: "right" } },
        { id: EliminationExceptionsColumn.ACTIONS, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.LG,
    },
};

// Partial + fallback so the UI survives new reasons landing before their label
// (e.g. ambiguous_multi_counterparty_je from PRO-10411): those cannot appear
// from the engine until that change merges, but the map should not need to be
// exhaustive to compile against the current enum.
const REASON_LABELS: Partial<Record<EliminationExceptionReason, string>> = {
    [EliminationExceptionReason.unmatchedIc]: "Unmatched",
    [EliminationExceptionReason.inTransitTransfer]: "In transit",
};

const humanizeReason = (reason: string): string =>
    reason.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase());

const FACT_TYPE_LABELS: Record<EliminationFactType, string> = {
    [EliminationFactType.icBalanceSheet]: "Balance sheet",
    [EliminationFactType.icProfitLoss]: "P&L",
    [EliminationFactType.openingBalance]: "Opening balance",
    [EliminationFactType.ownershipEquity]: "Equity",
};

const parseReason = (
    value: string | null,
): EliminationExceptionReason | undefined =>
    Object.values(EliminationExceptionReason).find(
        (reason) => reason === value,
    );

const parseNumber = (value: string | null): number | undefined => {
    if (value === null) {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

export const EliminationExceptionsPreviewPage: React.FC = () => {
    const { filters, setFilters, ledgerBasis } = useAccountingTabContext();
    const {
        openDrillDown,
        closeDrillDown,
        isOpen: isGlDrawerOpen,
        drawerKey,
        drawerTarget,
    } = useGlDrilldownDrawerState();
    const { consolidationGroup, parentEntity } = useConsolidatedReportView({
        filters,
        setFilters,
    });
    const [searchParams] = useSearchParams();
    const selectedAccountCode = parseNumber(searchParams.get("accountCode"));
    const selectedEntityId = parseNumber(searchParams.get("entityId"));
    const selectedReason = parseReason(searchParams.get("reason"));
    const { openOverlay } = useOverlays();
    const entitiesById = useEntitiesById();
    const allEntities = useEntities();
    const [isEliminatingDrawerOpen, setIsEliminatingDrawerOpen] =
        useState(false);

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_ELIMINATION_EXCEPTIONS_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const memberEntities = useMemo(() => {
        if (!parentEntity || !consolidationGroup) {
            return [];
        }
        const memberUuids = new Set(
            consolidationGroup.members.map((member) => member.entityUuid),
        );
        const otherMembers = allEntities.filter(
            (entity) =>
                memberUuids.has(entity.uuid) &&
                entity.uuid !== parentEntity.uuid,
        );
        return [parentEntity, ...otherMembers];
    }, [allEntities, consolidationGroup, parentEntity]);

    const canAuthorEliminations = Boolean(
        filters.consolidationGroupId && filters.workspaceId && parentEntity,
    );

    const handleCreateJournalEntry = useCallback(
        (row: EliminationExceptionsViewRow) => {
            const entity = entitiesById.get(row.linkEntityId);
            if (!entity) {
                return;
            }
            openOverlay("journalEntryDrawer", {
                entity,
                initialDate: row.date ?? undefined,
            });
        },
        [entitiesById, openOverlay],
    );

    const handleCreateEliminatingJournalEntry = useCallback(
        (_row: EliminationExceptionsViewRow) => {
            if (!canAuthorEliminations) {
                return;
            }
            setIsEliminatingDrawerOpen(true);
        },
        [canAuthorEliminations],
    );

    const tableMeta = useMemo<EliminationExceptionsTableMeta>(
        () => ({
            callbacks: {
                onCreateJournalEntry: handleCreateJournalEntry,
                onCreateEliminatingJournalEntry:
                    handleCreateEliminatingJournalEntry,
                onDrillDown: openDrillDown,
            },
        }),
        [
            handleCreateJournalEntry,
            handleCreateEliminatingJournalEntry,
            openDrillDown,
        ],
    );

    const exceptions = useEliminationExceptionsReportQuery({
        workspaceId: filters.workspaceId,
        consolidationGroupId: filters.consolidationGroupId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        entityId: selectedEntityId,
        accountCode: selectedAccountCode,
        reason: selectedReason,
    });

    // Landing on a bare /accounting/elimination-exceptions URL (no consolidated
    // params) would render an empty page. Backfill the active group's params —
    // consolidationGroupId also derives workspaceId in the filters — so the
    // report shows without the user having to drill in from a consolidated
    // report. Mirrors the elimination-detail page.
    useEffect(() => {
        if (filters.consolidationGroupId || !consolidationGroup) {
            return;
        }
        setFilters((current) => ({
            ...current,
            consolidationGroupId: consolidationGroup.id,
            entityId: current.entityId ?? parentEntity?.id,
            ledgerId:
                current.ledgerId ??
                consolidationGroup.overlayLedgerId ??
                undefined,
        }));
    }, [
        filters.consolidationGroupId,
        consolidationGroup,
        parentEntity,
        setFilters,
    ]);

    useEffect(() => {
        setIsEliminatingDrawerOpen(false);
    }, [filters.consolidationGroupId, filters.workspaceId]);

    const rows = useMemo<EliminationExceptionsViewRow[]>(() => {
        const mapped = (exceptions.data?.rows ?? []).map((row) => {
            const [primarySide] = row.sides;
            return {
                id: row.id,
                reason: row.reason,
                reasonLabel:
                    REASON_LABELS[row.reason] ?? humanizeReason(row.reason),
                isInformational: row.isInformational,
                entityName: row.entityName,
                counterpartyEntityName: row.otherEntityName,
                counterpartyName: row.counterpartyName ?? "",
                date: row.date,
                factTypeLabel: FACT_TYPE_LABELS[row.factType] ?? "",
                description: row.description,
                journalEntries: row.journalEntries,
                accountLabel: row.sides
                    .map((side) => {
                        const code = preferCodeText(
                            side.accountCodeText,
                            side.accountCode,
                        );
                        return code !== null
                            ? `${code} · ${side.accountName}`
                            : side.accountName;
                    })
                    .join(" / "),
                sourceLabel:
                    row.source === "transaction"
                        ? "Transaction"
                        : "Manual entry",
                netAmount: row.netAmount,
                startDate: filters.startDate,
                endDate: filters.endDate,
                linkEntityId: primarySide?.entityId ?? row.pair.entityId,
                linkAccountCode: primarySide?.accountCode ?? undefined,
                transactionIds: row.transactionIds,
            };
        });

        // Deterministic display order: date, then entity, then reason. Rows
        // with no resolved date sort last.
        return [...mapped].sort((a, b) => {
            if (a.date !== b.date) {
                if (a.date === null) {
                    return 1;
                }
                if (b.date === null) {
                    return -1;
                }
                return a.date.localeCompare(b.date);
            }
            const byEntity = a.entityName.localeCompare(b.entityName);
            if (byEntity !== 0) {
                return byEntity;
            }
            return a.reasonLabel.localeCompare(b.reasonLabel);
        });
    }, [exceptions.data?.rows, filters.startDate, filters.endDate]);

    const passThroughCount = exceptions.data?.passThroughs.length ?? 0;
    const generationDate = useConstant(() => new Date());
    const footerText = useGeneratedReportFooterText({ generationDate });
    const chrome = useMemo(
        () =>
            buildLocalReportChrome({
                title: parentEntity
                    ? `${parentEntity.name} (Consolidated)`
                    : "Consolidated",
                subtitle: "Intercompany exceptions",
                subtitle2: getOptionalReportDateRangeLabel(
                    filters.startDate,
                    filters.endDate,
                ),
                footer: footerText,
            }),
        [parentEntity, filters.startDate, filters.endDate, footerText],
    );

    return (
        <>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                consolidationGroup={consolidationGroup}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                requireDateSelection
                hideSearch
                hideClassFilter
                showCyclesSelect={false}
                hideLedgerSelect
                hideCreateJournalEntryButton
                hideEntityFilter
            />
            <LoaderOnInitialLoading queryResult={exceptions} dimWhileFetching>
                {filters.consolidationGroupId && filters.workspaceId ? (
                    <Card stacked sharpEdges fullPage>
                        <CardSection className={styles.header}>
                            <AccountingReportHeader chrome={chrome} />
                        </CardSection>
                        <ViewTable
                            data={rows}
                            view={defaultView}
                            config={eliminationExceptionsViewConfig}
                            className={styles.exceptionsTable}
                            emptyView={() => (
                                <EmptyState header="No intercompany exceptions for this period" />
                            )}
                            rowBorders={false}
                            enableColumnResizing
                            tableMeta={tableMeta}
                        />
                        {passThroughCount > 0 && (
                            <div className={styles.passThroughNote}>
                                {`${pluralizeWithCount(
                                    "intercompany P&L pass-through",
                                    passThroughCount,
                                )} — real third-party activity, not an imbalance`}
                            </div>
                        )}
                        <AccountingReportFooter chrome={chrome} />
                    </Card>
                ) : (
                    <EmptyState header="Select All entities (Consolidated) to view intercompany exceptions" />
                )}
            </LoaderOnInitialLoading>
            {canAuthorEliminations &&
                parentEntity &&
                filters.workspaceId &&
                filters.consolidationGroupId && (
                    <EliminatingEntryDrawer
                        open={isEliminatingDrawerOpen}
                        onClose={() => setIsEliminatingDrawerOpen(false)}
                        workspaceId={filters.workspaceId}
                        groupId={filters.consolidationGroupId}
                        entities={memberEntities}
                    />
                )}
            {drawerTarget && (
                <GlDrilldownDrawer
                    key={drawerKey}
                    open={isGlDrawerOpen}
                    onClose={closeDrillDown}
                    filters={drawerTarget.filters}
                    entityIds={drawerTarget.entityIds}
                    ledgerBasis={ledgerBasis}
                    classifications={drawerTarget.classifications}
                    pnlBalanceSince={drawerTarget.pnlBalanceSince}
                />
            )}
        </>
    );
};
